import type { ModelSelection, ServerProvider } from "@cmmd-forge/contracts";
import { useEffect } from "react";

import {
  fetchForgeProviderAccess,
  providerPolicyForAccess,
  useForgeProviderAccess,
} from "~/cmmd/forgeProviderAccess";
import { resolveRunnableProviderSnapshots } from "~/cmmd/providerAvailabilityJoin";
import { useShellProviderAvailability } from "~/cmmd/useShellProviderAvailability";
import {
  cmmdRuntimeInventoryResource,
  useCmmdRuntimeInventorySnapshot,
} from "~/cmmd/cmmdRuntimeInventoryResource";
import {
  fetchProjectLinkListSnapshot,
  type ProjectLinkRow,
  useProjectLinkListSnapshot,
} from "~/cmmd/projectLinkListResource";
import { buildComposerModelOptionsByProvider } from "~/components/chat/ChatComposer.logic";
import {
  isVisibleProviderModelSlug,
  isVisibleProviderPickerKind,
} from "~/components/chat/ProviderModelPicker";
import { providersFromLastKnownCatalog } from "~/components/newThread/lastKnownProviderCatalog";
import { resolveNewThreadModelSelection } from "~/components/newThread/NewThreadLanding.logic";
import { classifyEnvironmentId } from "~/environments/environmentIdClassification";

import type { AutomationDraft } from "./automationFormModel";

const EMPTY_PROVIDERS: readonly ServerProvider[] = [];

type ProjectInventoryStatus = "ready" | "missing" | "ambiguous" | "unpublished";

type ProjectInventory = {
  readonly providers: readonly ServerProvider[];
  readonly runtimeId: string | null;
  readonly status: ProjectInventoryStatus;
};

function projectInventory(input: {
  readonly projectId: string;
  readonly projectLinks: readonly ProjectLinkRow[];
  readonly runtimes: ReturnType<typeof useCmmdRuntimeInventorySnapshot>["inventory"];
}): ProjectInventory {
  const projectId = Number(input.projectId);
  if (!Number.isFinite(projectId)) {
    return { providers: EMPTY_PROVIDERS, runtimeId: null, status: "missing" };
  }
  const matchingRuntimeIds = new Set(
    input.projectLinks.flatMap((link) => {
      if (link.cmmdProjectId !== projectId) return [];
      const runtimeId = classifyEnvironmentId(link.forgeEnvironmentId).legacyRuntimeId;
      return runtimeId === null ? [] : [runtimeId];
    }),
  );
  const matchingRuntimes =
    input.runtimes?.runtimes.filter(
      (entry) => entry.runtime.source === "forge-cloud" && matchingRuntimeIds.has(entry.runtime.id),
    ) ?? [];
  if (matchingRuntimes.length > 1) {
    return { providers: EMPTY_PROVIDERS, runtimeId: null, status: "ambiguous" };
  }
  const runtime = matchingRuntimes[0];
  if (!runtime) return { providers: EMPTY_PROVIDERS, runtimeId: null, status: "missing" };
  if (runtime.inventoryPublishedAt === null || runtime.providers === undefined) {
    return { providers: EMPTY_PROVIDERS, runtimeId: runtime.runtime.id, status: "unpublished" };
  }
  return {
    providers:
      providersFromLastKnownCatalog({
        catalog: runtime.providers,
        publishedAt: runtime.inventoryPublishedAt,
      }) ?? EMPTY_PROVIDERS,
    runtimeId: runtime.runtime.id,
    status: "ready",
  };
}

function visibleProviders(providers: readonly ServerProvider[]): readonly ServerProvider[] {
  return providers.flatMap((provider) => {
    if (!isVisibleProviderPickerKind(provider.provider)) return [];
    const models = provider.models.filter((model) => isVisibleProviderModelSlug(model.slug));
    return models.length > 0 ? [{ ...provider, models }] : [];
  });
}

export function exactSelectionAvailable(
  selection: ModelSelection | null,
  providers: readonly ServerProvider[],
): boolean {
  if (!selection) return false;
  return providers.some(
    (provider) =>
      provider.provider === selection.provider &&
      provider.models.some((model) => model.slug === selection.model),
  );
}

export function automationResponseMatchesSelection(input: {
  readonly expectedId?: number;
  readonly expectedProjectId: number;
  readonly response: {
    readonly id: number;
    readonly projectId: number;
    readonly modelSelection: { readonly provider: string; readonly model: string } | null;
  } | null;
  readonly selection: ModelSelection;
}): boolean {
  return (
    input.response !== null &&
    (input.expectedId === undefined || input.response.id === input.expectedId) &&
    input.response.projectId === input.expectedProjectId &&
    input.response.modelSelection?.provider === input.selection.provider &&
    input.response.modelSelection.model === input.selection.model
  );
}

export async function validateAutomationModelSelection(draft: AutomationDraft): Promise<boolean> {
  if (draft.runLocation === "local") return true;
  if (draft.modelSelection === null) return false;
  try {
    const [inventory, projectLinks] = await Promise.all([
      cmmdRuntimeInventoryResource.refresh({ allWorkspaces: true }),
      fetchProjectLinkListSnapshot(),
    ]);
    if (!projectLinks.loaded) return false;
    const selectedProjectInventory = projectInventory({
      projectId: draft.selectedProjectId,
      projectLinks: projectLinks.links,
      runtimes: inventory,
    });
    if (selectedProjectInventory.runtimeId === null) return false;
    const access = await fetchForgeProviderAccess("cmmd-cloud", selectedProjectInventory.runtimeId);
    const providers = visibleProviders(selectedProjectInventory.providers);
    // Config time is Run time (forge#3396): only a provider that can execute
    // may be configured, judged by the join's one evaluation.
    const runnableProviders = resolveRunnableProviderSnapshots({
      providers,
      access,
      credentialMode: "cmmd-cloud",
      hasLiveInventory: true,
    });
    return exactSelectionAvailable(draft.modelSelection, runnableProviders);
  } catch {
    return false;
  }
}

export function useAutomationProviderSelection(input: { readonly draft: AutomationDraft }) {
  const cloudEnabled = input.draft.runLocation === "cloud";
  const inventory = useCmmdRuntimeInventorySnapshot();
  const projectLinks = useProjectLinkListSnapshot();

  useEffect(() => {
    if (!cloudEnabled) return;
    void cmmdRuntimeInventoryResource.refresh({ allWorkspaces: true }).catch(() => undefined);
  }, [cloudEnabled, input.draft.selectedProjectId]);

  const selectedProjectInventory = projectInventory({
    projectId: input.draft.selectedProjectId,
    projectLinks: projectLinks.links,
    runtimes: inventory.inventory,
  });
  const access = useForgeProviderAccess({
    enabled: cloudEnabled,
    credentialMode: "cmmd-cloud",
    runtimeId: selectedProjectInventory.runtimeId,
  });
  const publishedProviders = visibleProviders(selectedProjectInventory.providers);
  // Config time is Run time (forge#3396): the stored/effective selection is
  // only valid when its provider can execute, so the gate and the model
  // options are drawn from the join's Run projection, not a display filter.
  const runnableProviders = resolveRunnableProviderSnapshots({
    providers: publishedProviders,
    access,
    credentialMode: "cmmd-cloud",
    hasLiveInventory: true,
  });
  const selection = resolveNewThreadModelSelection({
    providers: publishedProviders,
    preferredProvider: input.draft.modelSelection?.provider ?? null,
    preferredModel: input.draft.modelSelection?.model ?? null,
    providerAccess: access,
    credentialMode: "cmmd-cloud",
  });
  const storedSelectionValid = exactSelectionAvailable(
    input.draft.modelSelection,
    runnableProviders,
  );
  const modelOptionsByProvider = buildComposerModelOptionsByProvider(
    runnableProviders,
    providerPolicyForAccess(access),
  );
  /**
   * Why the picker may withhold each provider it shows, for the selected
   * project's Environment. The picker's own input is `publishedProviders`,
   * not `runnableProviders`, and the rows answer the Run projection: the
   * config gate refuses on the Run verdict, so the row's reason must be the
   * same verdict's, transient readiness included, or the form would show a
   * provider as usable while refusing to save it.
   */
  const providerAvailability = useShellProviderAvailability(
    {
      providers: publishedProviders,
      policy: providerPolicyForAccess(access),
      access,
      credentialMode: "cmmd-cloud",
      hasLiveInventory: selectedProjectInventory.status === "ready",
    },
    "run",
  );

  return {
    access,
    runnableProviders,
    providers: publishedProviders,
    providerAvailability,
    inventoryError: inventory.error,
    projectInventoryStatus: selectedProjectInventory.status,
    inventoryLoading: inventory.isLoading,
    modelOptionsByProvider,
    orgProviderPolicy: providerPolicyForAccess(access),
    selection,
    storedSelectionValid,
    isSelectionValid(selection: ModelSelection) {
      return exactSelectionAvailable(selection, runnableProviders);
    },
  };
}
